import { useState, useEffect } from "react";
import {
  Box,
  Card,
  Typography,
  Button,
  Dialog,
  CardContent,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Grid,
  DialogActions,
  TextField,
  Divider,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  createStoreRequest,
  updateStoreRequest,
} from "../../../redux/StoreList/StoreSlice";
import { RxCross2 } from "react-icons/rx";
import AddIcon from "@mui/icons-material/Add";
import CreateStoreTableList from "./CreateStoreTableList/CreateStoreTableList";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import auth from "../../../configs/auth";
import { getActiveCountriesRequest } from "../../../redux/CountryList/CountrySlice";
import { canShowPermission } from "../Permissions/permissionObject";
import InfoButton from "../../Comman/InfoButton";

const initialFormData = {
  jeweller: "",
  branchName: "",
  address1: "",
  address2: "",
  address3: "",
  area: "",
  state: "",
  city: "",
  pincode: "",
  managerName: "",
  phone: "",
  countryCode: "",
  email: "",
  external_identifier: "",
};

const CreateStoreList = () => {
  const dispatch = useDispatch();
  const { stores = [] } = useSelector((state) => state.store || {});
  const { activeCountries = [] } = useSelector((state) => state.country || {});
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("add");
  const [selectedStore, setSelectedStore] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [isSaving, setIsSaving] = useState(false); // Loader state
  const [selectedDialCode, setSelectedDialCode] = useState("");
  const [selectedPhoneLength, setSelectedPhoneLength] = useState(10);
  const [countryStates, setCountryStates] = useState([]);

  const jeweller = sessionStorage.getItem(auth.selectedJeweller);
   const [selectedJewellerId, setSelectedJewellerId] = useState(() => {
        return sessionStorage.getItem(auth.selectedJeweller) || "";
      });
    const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
    const jewellerID = storedUser?.jeweller;
    const finalJewellerId =
      selectedJewellerId && selectedJewellerId.trim() !== ""
        ? selectedJewellerId
        : jewellerID;
  useEffect(() => {
    dispatch(getActiveCountriesRequest({ jeweller :finalJewellerId}));
  }, [dispatch]);

  const [permissions, setPermissions] = useState({});
  useEffect(() => {
    const storedPermissions = localStorage.getItem("userPermissions");
    if (storedPermissions) {
      try {
        setPermissions(JSON.parse(storedPermissions));
      } catch (e) {
        console.error("Invalid permissions JSON", e);
      }
    }
  }, []);

  const canCreateStore = canShowPermission(permissions, "storemanagement", "create");

  // Open dialog
  const handleOpenDialog = () => {
    setMode("add");
    setFormData(initialFormData);
    setOpen(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setOpen(false);
    setSelectedStore(null);
    setFormData(initialFormData);
    setIsSaving(false);
  };

  // Edit store
  const handleEditStore = (store) => {
    setSelectedStore(store);
    setMode("edit");
    setFormData({
      ...store,
      countryCode: store.countryCode?._id || "",
    });
    setSelectedDialCode(store.countryCode?.dialCode || "+91");
    setSelectedPhoneLength(store.countryCode?.phoneLength || 10);
    setOpen(true);
  };

  // Validation helpers
  const isValidEmail = (email) => /\S+@\S+\.\S+/.test(email);
  const isValidPhone = (phone) => /^\d{10}$/.test(phone);
  const isValidPincode = (pincode) =>
    pincode === "" ||
    (/^\d{5,10}$/.test(pincode) && pincode.length >= 5 && pincode.length <= 10);
  const isValidStoreName = (branchName) =>
    /^[A-Za-z][A-Za-z0-9\s]*$/.test(branchName);

  // Input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "pincode" && /^\d{5,10}$/.test(value)) {
      fetchCityFromPincode(value);
    }
  };

  const fetchCityFromPincode = async (pincode) => {
    try {
      const res = await fetch(
        `https://api.postalpincode.in/pincode/${pincode}`
      );
      const data = await res.json();

      if (data[0]?.Status === "Success" && data[0]?.PostOffice?.length > 0) {
        const city = data[0].PostOffice[0].District;
        const state = data[0].PostOffice[0].State;

        setFormData((prev) => ({
          ...prev,
          city: city || prev.city,
          state: state || prev.state,
        }));
      }
    } catch (error) {
      console.error("Error fetching city/state from pincode:", error);
    }
  };

  useEffect(() => {
    if (mode !== "edit") return;
    if (!formData.countryCode) return;
    if (activeCountries.length === 0) return;

    const selectedCountry = activeCountries.find(
      (c) => c._id === formData.countryCode
    );

    if (!selectedCountry) return;

    setCountryStates(selectedCountry.states || []);
    setSelectedDialCode(selectedCountry.dialCode || "+91");
    setSelectedPhoneLength(selectedCountry.phoneLength || 10);
  }, [mode, formData.countryCode, activeCountries]);

  // Add/Edit store
  const handleAddOrEditStore = () => {
    if (isSaving) return;

    const trimmedData = Object.fromEntries(
      Object.entries(formData).map(([k, v]) => [
        k,
        typeof v === "string" ? v.trim() : v,
      ])
    );

    const {
      branchName,
      address1,
      state,
      city,
      area,
      pincode,
      managerName,
      phone,
      countryCode,
      email,
    } = trimmedData;

    // Validation
    if (!branchName) return toast.error("Store Name is required.");
    if (!isValidStoreName(branchName))
      return toast.error(
        "Store Name must start with a letter, not numbers or spaces."
      );
    if (!address1) return toast.error("Address 1 is required.");
    if (!area) return toast.error("Area is required.");
    if (!state) return toast.error("State is required.");
    if (!city) return toast.error("City is required.");
    if (pincode && !isValidPincode(pincode))
      return toast.error("Pincode must be between 5 and 10 digits.");
    if (!managerName) return toast.error("Manager Name is required.");
    if (!phone) return toast.error("Phone number is required.");
    // if (!isValidPhone(phone))
    //   return toast.error("Please enter a valid 10-digit phone number.");
    if (phone.length !== selectedPhoneLength) {
      toast.error(`Phone number must be exactly ${selectedPhoneLength} digits`);
      return;
    }
    if (!email) return toast.error("Email is required.");
    if (!isValidEmail(email))
      return toast.error("Please enter a valid email address.");

    const payload = { ...trimmedData, _id: selectedStore?._id };

    setIsSaving(true);

    setTimeout(() => {
      if (mode === "add") {
        const emailExists = stores.some((store) => store.email === email);
        const phoneExists = stores.some((store) => store.phone === phone);

        if (emailExists) {
          toast.error("Email already exists. Please use a different email.");
          setIsSaving(false);
          return;
        }
        if (phoneExists) {
          toast.error(
            "Phone number already exists. Please use a different number."
          );
          setIsSaving(false);
          return;
        }

        dispatch(createStoreRequest(payload));
        toast.success("Store created successfully!");
      } else {
        dispatch(updateStoreRequest(payload));
        toast.success("Store updated successfully!");
      }

      handleCloseDialog();
      setIsSaving(false);
    }, 300);
  };

  return (
    <Box sx={{ width: "98%" }}>
      <Box sx={{position: "relative"}}>
      <ToastContainer />
      <Box
        sx={{
          width: "100%",
          height: "100%",
          mt: { xs: 10, md: 10 },
          mb: { xs: 2, md: 2 },
          background: "#fff",
          borderRadius: "3px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Box sx={{ justifyContent: "space-between", display: "flex", p: 2 }}>
          <Typography
            fontFamily={"Aeonik"}
            sx={{ fontSize: "24px", fontWeight: 700 }}
          >
            Store List
          </Typography>
          {canCreateStore && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{
                textTransform: "none",
                background:
                  "linear-gradient(90deg, #7028DA 0%, #9E3ED7 76.93%)",
                fontFamily: "inherit",
              }}
              onClick={handleOpenDialog}
            >
              New Store
            </Button>
          )}
        </Box>
        <CardContent sx={{ width: "100%" }}>
          <CreateStoreTableList onEdit={handleEditStore} />
        </CardContent>
      </Box>

      {/* Dialog */}
      <Dialog
        open={open}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="lg"
        PaperProps={{ sx: { borderRadius: "3px" } }}
      >
        <Box sx={{ overflowY: "auto" }}>
          <Box
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              cursor: "pointer",
              background: "#E2E2EA",
              borderRadius: "50%",
              width: 26,
              height: 26,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#646464",
            }}
            onClick={handleCloseDialog}
          >
            <RxCross2 />
          </Box>

          <Typography
            fontWeight={700}
            fontSize={24}
            p={2}
            sx={{
              background: "linear-gradient(90deg, #7028DA 0%, #9E3ED7 76.93%)",
              color: "#fff",
            }}
            fontFamily={"Aeonik"}
          >
            {mode === "edit" ? "Edit Store" : "Create Store"}
          </Typography>

          <Divider />
          <Box p={2}>
            <Grid container spacing={2} mt={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  sx={{ width: "350px" }}
                  label={
                    <>
                      <span>Store Name</span>{" "}
                      <span style={{ color: "red" }}>*</span>
                    </>
                  }
                  size="small"
                  name="branchName"
                  placeholder="Store Name"
                  value={formData.branchName}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} mt={3}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  sx={{ width: "350px" }}
                  label={
                    <>
                      <span>Address 1</span>{" "}
                      <span style={{ color: "red" }}>*</span>
                    </>
                  }
                  fullWidth
                  size="small"
                  name="address1"
                  placeholder="Enter Address 1"
                  value={formData.address1}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  sx={{ width: "350px" }}
                  label="Address 2"
                  fullWidth
                  size="small"
                  name="address2"
                  placeholder="Enter Address 2"
                  value={formData.address2}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  sx={{ width: "350px" }}
                  label="Address 3"
                  fullWidth
                  size="small"
                  name="address3"
                  placeholder="Enter Address 3"
                  value={formData.address3}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} mt={3}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  sx={{ width: "350px" }}
                  label={
                    <>
                      <span>Area</span> <span style={{ color: "red" }}>*</span>
                    </>
                  }
                  fullWidth
                  size="small"
                  name="area"
                  placeholder="Your Area"
                  value={formData.area}
                  onChange={handleChange}
                />
              </Grid>

                <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small" sx={{ width: "350px" }}>
                  <InputLabel id="country-select-label">
                    <span>Select Country</span>
                    <span style={{ color: "red" }}>*</span>
                  </InputLabel>
                  <Select
                    labelId="country-select-label"
                    value={formData.countryCode || ""}
                    onChange={(e) => {
                      const selected = activeCountries.find(
                        (c) => c._id === e.target.value
                      );
                      setSelectedDialCode(selected?.dialCode || "+91");
                      setSelectedPhoneLength(selected?.phoneLength || 10);
                      setFormData((prev) => ({
                        ...prev,
                        countryCode: selected?._id || "",
                      }));
                      setCountryStates(selected?.states || []);
                    }}
                    label="Select Country"
                  >
                    <MenuItem value="" disabled>
                      <em>Select Country</em>
                    </MenuItem>
                    {activeCountries.map((country) => (
                      <MenuItem key={country._id} value={country._id}>
                        {`${country.countryCode} (${country.countryName})`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel id="state-select">
                    {" "}
                    <span>Select State</span>
                    <span style={{ color: "red" }}>*</span>
                  </InputLabel>
                  <Select
                    sx={{ width: "350px" }}
                    labelId="state-select"
                    id="state-select-id"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    label="Select State"
                  >
                    <MenuItem value="" disabled>
                      <em>Select State</em>
                    </MenuItem>
                    {countryStates.map((state) => (
                      <MenuItem key={state} value={state}>
                        {state}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  sx={{ width: "350px" }}
                  label={
                    <>
                      <span>City</span> <span style={{ color: "red" }}>*</span>
                    </>
                  }
                  type="text"
                  fullWidth
                  size="small"
                  name="city"
                  placeholder="City"
                  value={formData.city}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  sx={{ width: "350px" }}
                  label={
                    <>
                      <span>Pincode</span>{" "}
                      <span style={{ color: "red" }}></span>
                    </>
                  }
                  fullWidth
                  size="small"
                  name="pincode"
                  placeholder="Enter Pincode"
                  value={formData.pincode}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} mt={3}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  sx={{ width: "350px" }}
                  label={
                    <>
                      <span>Manager Name</span>{" "}
                      <span style={{ color: "red" }}>*</span>
                    </>
                  }
                  fullWidth
                  size="small"
                  name="managerName"
                  placeholder="Manager Name"
                  value={formData.managerName}
                  onChange={handleChange}
                />
              </Grid>

              {/* Phone Field */}
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  sx={{ width: "350px" }}
                  label={
                    <>
                      <span>Phone</span> <span style={{ color: "red" }}>*</span>
                    </>
                  }
                  type="tel"
                  fullWidth
                  size="small"
                  name="phone"
                  placeholder={`Enter ${selectedPhoneLength}-digit phone number`}
                  value={formData.phone}
                  onChange={(e) => {
                    const numberOnly = e.target.value.replace(/\D/g, "");
                    if (numberOnly.length <= selectedPhoneLength) {
                      setFormData((prev) => ({ ...prev, phone: numberOnly }));
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <Typography sx={{ mr: 1 }}>{selectedDialCode}</Typography>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  sx={{ width: "350px" }}
                  label="Email ID"
                  fullWidth
                  size="small"
                  name="email"
                  placeholder="Enter Email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>

            <DialogActions sx={{ mt: 2 }}>
              <Button
                onClick={handleAddOrEditStore}
                variant="contained"
                sx={{
                  background:
                    "linear-gradient(90deg, #7028DA 0%, #9E3ED7 76.93%)",
                  px: 6,
                  textTransform: "none",
                }}
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : mode === "edit" ? "Update" : "Add"}
              </Button>
            </DialogActions>
          </Box>
        </Box>
      </Dialog>
    </Box>
    <Box
    sx={{
      position: "fixed",
      bottom: 24,
      right: 24,
      zIndex: 1000,
    }}
  >
    <InfoButton pageName="STORE" />
  </Box>
  </Box>
  );
};

export default CreateStoreList;
